// client/src/components/event/EventAfterPanel.jsx
//
// /:tenant/:eventId/after — what happens after an event (Growth Phase 9,
// feature_key "post_event"), reached from the Event Hub:
//
//   Follow-up   thank-you on/off + delay, survey form, photo collection,
//               auto-drafted recap. The ticket-followup worker sends at
//               end + delay: thank-you (+ survey button, + the attendee's own
//               photo link) to checked-in attendees, a different message to
//               no-shows.
//   Survey      results from the picked Phase 4 form (rating average, NPS,
//               response rate, free text).
//   Photos      attendee uploads waiting for review. Only approved photos the
//               uploader agreed to share can go in a recap.
//   Recap       stats + suggested text + photos → a DRAFT post. "Open in
//               scheduler" hands it to the normal scheduler; nothing is
//               posted until someone schedules it (and approves it, when the
//               tenant requires approval).
//
// API: services/postEventApi.js → server/routes/postEventRoutes.js

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import { getSessionLocal } from '../../services/sessionLocal';
import { can } from '../../services/rolePermissions';
import {
  DELAY_CHOICES, errorText, getAfter, getRecap, getSurveyResults, isNotReady,
  listEventPhotos, moderateEventPhoto, saveAfterSettings, saveRecapDraft,
} from '../../services/postEventApi';
import '../analytics/PerformancePanel.css';
import '../forms/Forms.css';
import './EventAfter.css';

const PHOTO_TABS = ['pending', 'approved', 'rejected'];

function money(n, currency = 'USD') {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(n) || 0);
  } catch {
    return `${Math.round(Number(n) || 0)} ${currency}`;
  }
}

export default function EventAfterPanel({ tenantId, eventId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role } = getSessionLocal();
  const canEdit = can(role, 'events', 'update');

  const [after, setAfter] = useState(null);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await getAfter(tenantId, eventId);
      setAfter(res);
      setDraft({
        thankYouEnabled: res.settings.thankYouEnabled,
        delayHours: res.settings.delayHours,
        surveyFormId: res.settings.surveyFormId || '',
        collectPhotos: res.settings.collectPhotos,
        autoDraftRecap: res.settings.autoDraftRecap,
      });
    } catch (err) {
      setError(t('postEvent.loadError', 'Could not load the After section.'));
    }
  }, [tenantId, eventId, t]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setNotice(null);
    try {
      const res = await saveAfterSettings(tenantId, eventId, {
        ...draft,
        delayHours: Number(draft.delayHours),
        surveyFormId: draft.surveyFormId ? Number(draft.surveyFormId) : null,
      });
      setAfter((prev) => ({ ...prev, settings: res.settings }));
      setNotice({ kind: 'ok', text: t('postEvent.saved', 'Saved.') });
    } catch (err) {
      setNotice({
        kind: 'error',
        text: isNotReady(err)
          ? t('postEvent.notReady', 'This needs a database update before it can be used.')
          : errorText(err, t('postEvent.saveError', 'Could not save.')),
      });
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className="ea-page"><ErrorState message={error} onRetry={load} /></div>;
  if (!after || !draft) return <div className="ea-page"><LoadingState /></div>;

  const status = after.settings.status;
  const readyAll = after.ready || {};

  return (
    <div className="ea-page">
      <section className="surface">
        <h2>{t('postEvent.title', 'After the event')}</h2>
        <p className="surface-muted perf-small">
          {t('postEvent.intro', 'Thank the people who came, ask how it went, collect their photos and draft a recap post. Nothing is posted without someone on your team scheduling it.')}
        </p>
      </section>

      {!readyAll.settings && (
        <div className="note warn" role="status">{t('postEvent.notReady', 'This needs a database update before it can be used.')}</div>
      )}

      <section className="surface" aria-labelledby="ea-followup-h">
        <h4 id="ea-followup-h">{t('postEvent.followup.title', 'Follow-up email')}</h4>
        <form onSubmit={save}>
          <div className="ea-settings">
            <label className="fo-check">
              <input type="checkbox" checked={draft.thankYouEnabled} disabled={!canEdit}
                onChange={(e) => setDraft({ ...draft, thankYouEnabled: e.target.checked })} />
              {t('postEvent.followup.thankYou', 'Send a thank-you to people who checked in, and a "sorry we missed you" to no-shows')}
            </label>
            <label className="fo-field">
              {t('postEvent.followup.delay', 'Send')}
              <select value={draft.delayHours} disabled={!canEdit}
                onChange={(e) => setDraft({ ...draft, delayHours: Number(e.target.value) })}>
                {DELAY_CHOICES.map((h) => (
                  <option key={h} value={h}>
                    {h === 0
                      ? t('postEvent.followup.delayNow', 'Right after the event ends')
                      : t('postEvent.followup.delayHours', '{{count}} hours after it ends', { count: h })}
                  </option>
                ))}
              </select>
            </label>
            <label className="fo-field">
              {t('postEvent.followup.survey', 'Survey')}
              <select value={draft.surveyFormId} disabled={!canEdit}
                onChange={(e) => setDraft({ ...draft, surveyFormId: e.target.value })}>
                <option value="">{t('postEvent.followup.noSurvey', 'No survey')}</option>
                {(after.surveyForms || []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}{f.hasSurveyFields ? '' : ` ${t('postEvent.followup.noRating', '(no rating question)')}`}
                  </option>
                ))}
              </select>
            </label>
            <label className="fo-check">
              <input type="checkbox" checked={draft.collectPhotos} disabled={!canEdit || !readyAll.photos}
                onChange={(e) => setDraft({ ...draft, collectPhotos: e.target.checked })} />
              {t('postEvent.followup.photos', 'Ask attendees to share their photos (each gets their own upload link)')}
            </label>
            <label className="fo-check">
              <input type="checkbox" checked={draft.autoDraftRecap} disabled={!canEdit || !readyAll.recap}
                onChange={(e) => setDraft({ ...draft, autoDraftRecap: e.target.checked })} />
              {t('postEvent.followup.autoRecap', 'Draft a recap post when the follow-up goes out (draft only)')}
            </label>
          </div>
          <p className="surface-muted perf-small">
            {t('postEvent.followup.surveyHint', 'Surveys are forms. Add a rating or NPS question to a form for this event, then pick it here.')}{' '}
            <button type="button" className="lm-btn secondary" onClick={() => navigate(`/${tenantId}/forms?event=${eventId}`)}>
              {t('postEvent.followup.manageForms', 'Manage forms')}
            </button>
          </p>
          {canEdit && (
            <div className="ea-actions">
              <button type="submit" className="lm-btn" disabled={saving || !readyAll.settings}>
                {saving ? t('postEvent.saving', 'Saving…') : t('postEvent.save', 'Save')}
              </button>
            </div>
          )}
        </form>
        {notice && <div className={`note ${notice.kind}`} role={notice.kind === 'error' ? 'alert' : 'status'}>{notice.text}</div>}
        <FollowupStatus t={t} status={status} />
      </section>

      <SurveyResults t={t} tenantId={tenantId} eventId={eventId} hasSurvey={!!after.settings.surveyFormId} />
      <PhotoModeration t={t} tenantId={tenantId} eventId={eventId} canEdit={canEdit} counts={after.photoCounts} />
      <RecapSection t={t} tenantId={tenantId} eventId={eventId} canEdit={canEdit} />
    </div>
  );
}

function FollowupStatus({ t, status }) {
  if (!status) return null;
  const sent = status.followup === 'sent';
  return (
    <div className={`note ${status.followup === 'failed' ? 'error' : 'info'}`} role="status">
      {sent
        ? t('postEvent.status.sent', 'Sent: {{attended}} thank-you emails, {{noShow}} no-show emails.', {
          attended: status.attended ?? 0, noShow: status.noShow ?? 0,
        })
        : t(`postEvent.status.${status.followup}`, status.followup)}
      {status.lastError ? ` ${status.lastError}` : ''}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Survey results
// ---------------------------------------------------------------------------

function SurveyResults({ t, tenantId, eventId, hasSurvey }) {
  const [results, setResults] = useState(undefined);
  useEffect(() => {
    let off = false;
    if (!hasSurvey) { setResults(null); return undefined; }
    getSurveyResults(tenantId, eventId)
      .then((r) => { if (!off) setResults(r.results || null); })
      .catch(() => { if (!off) setResults(null); });
    return () => { off = true; };
  }, [tenantId, eventId, hasSurvey]);

  return (
    <section className="surface" aria-labelledby="ea-survey-h">
      <h4 id="ea-survey-h">{t('postEvent.survey.title', 'Survey results')}</h4>
      {!hasSurvey && <p className="surface-muted perf-small">{t('postEvent.survey.none', 'No survey picked for this event.')}</p>}
      {hasSurvey && results === undefined && <LoadingState />}
      {hasSurvey && results === null && <p className="surface-muted perf-small">{t('postEvent.survey.noResults', 'No results yet.')}</p>}
      {results && (
        <>
          <div className="ea-stats">
            <Stat label={t('postEvent.survey.responses', 'Responses')} value={results.responses} />
            {results.responseRate != null && (
              <Stat label={t('postEvent.survey.rate', 'Response rate')} value={`${results.responseRate}%`} />
            )}
          </div>
          {results.fields.map((f) => <SurveyField key={f.key} t={t} field={f} />)}
        </>
      )}
    </section>
  );
}

function SurveyField({ t, field }) {
  if (field.type === 'rating' || field.type === 'nps') {
    const max = Math.max(1, ...field.distribution.map((d) => d.count));
    return (
      <div>
        <h4>{field.label}</h4>
        <p className="perf-small">
          {field.type === 'nps'
            ? t('postEvent.survey.npsLine', 'NPS {{nps}} · average {{avg}} of 10 · {{count}} answers', { nps: field.nps ?? '—', avg: field.average ?? '—', count: field.count })
            : t('postEvent.survey.ratingLine', 'Average {{avg}} of 5 · {{count}} answers', { avg: field.average ?? '—', count: field.count })}
        </p>
        <div className="ea-scale">
          {field.distribution.map((d) => (
            <div key={d.score} className="ea-scale-row">
              <span className="perf-num">{d.score}</span>
              <div className="perf-bar" aria-hidden="true"><div className="perf-bar-fill" style={{ width: `${(d.count / max) * 100}%` }} /></div>
              <span className="perf-num">{d.count}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === 'text') {
    return (
      <div>
        <h4>{field.label}</h4>
        {field.answers.length === 0
          ? <p className="surface-muted perf-small">{t('postEvent.survey.noAnswers', 'No answers.')}</p>
          : <ul className="ea-quotes">{field.answers.map((a, i) => <li key={i}>{a.text}</li>)}</ul>}
      </div>
    );
  }
  if (field.type === 'select') {
    const entries = Object.entries(field.counts || {});
    return (
      <div>
        <h4>{field.label}</h4>
        <ul className="perf-bars">
          {entries.map(([k, n]) => <li key={k} className="perf-bar-label"><span>{k}</span><span className="perf-num">{n}</span></li>)}
        </ul>
      </div>
    );
  }
  return null;
}

function Stat({ label, value }) {
  return (
    <div className="ea-stat">
      <div className="ea-stat-value">{value}</div>
      <div className="ea-stat-label">{label}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Photo moderation
// ---------------------------------------------------------------------------

function PhotoModeration({ t, tenantId, eventId, canEdit, counts: initialCounts }) {
  const [tab, setTab] = useState('pending');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await listEventPhotos(tenantId, eventId, tab));
    } catch {
      setData({ photos: [], counts: initialCounts || {} });
    }
  }, [tenantId, eventId, tab, initialCounts]);
  useEffect(() => { load(); }, [load]);

  const act = async (photo, status) => {
    setBusy(photo.id);
    setErr('');
    try {
      await moderateEventPhoto(tenantId, eventId, photo.id, status);
      await load();
    } catch (e) {
      setErr(errorText(e, t('postEvent.photos.error', 'Could not update the photo.')));
    } finally {
      setBusy(null);
    }
  };

  const counts = data?.counts || initialCounts || {};
  return (
    <section className="surface" aria-labelledby="ea-photos-h">
      <h4 id="ea-photos-h">{t('postEvent.photos.title', 'Attendee photos')}</h4>
      <p className="surface-muted perf-small">
        {t('postEvent.photos.intro', 'Every uploader agreed you may repost their photos. Approved photos join the event media; rejected ones are deleted.')}
      </p>
      <div className="fo-tabs" role="tablist">
        {PHOTO_TABS.map((s) => (
          <button key={s} type="button" role="tab" className="fo-tab" aria-selected={tab === s} onClick={() => setTab(s)}>
            {t(`postEvent.photos.tab.${s}`, s)} ({counts[s] || 0})
          </button>
        ))}
      </div>
      {err && <div className="note error" role="alert">{err}</div>}
      {!data && <LoadingState />}
      {data && data.photos.length === 0 && <p className="surface-muted perf-small">{t('postEvent.photos.empty', 'No photos here.')}</p>}
      {data && data.photos.length > 0 && (
        <ul className="ea-grid">
          {data.photos.map((p) => (
            <li key={p.id} className="ea-tile">
              <a href={p.url} target="_blank" rel="noopener noreferrer">
                <img src={p.thumbUrl} alt={t('postEvent.photos.alt', 'Photo from {{name}}', { name: p.submitterName || t('postEvent.photos.anon', 'an attendee') })} loading="lazy" />
              </a>
              <div className="ea-tile-body">
                <span className="ea-tile-name">{p.submitterName || p.submitterEmail || t('postEvent.photos.anon', 'an attendee')}</span>
                {p.consentToRepost && <span className="lm-badge">{t('postEvent.photos.consented', 'OK to repost')}</span>}
                {canEdit && p.status !== 'rejected' && (
                  <div className="ea-tile-actions">
                    {p.status === 'pending' && (
                      <button type="button" className="lm-btn" disabled={busy === p.id} onClick={() => act(p, 'approved')}>
                        {t('postEvent.photos.approve', 'Approve')}
                      </button>
                    )}
                    <button type="button" className="lm-btn secondary" disabled={busy === p.id} onClick={() => act(p, 'rejected')}>
                      {t('postEvent.photos.reject', 'Reject')}
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Recap
// ---------------------------------------------------------------------------

function RecapSection({ t, tenantId, eventId, canEdit }) {
  const navigate = useNavigate();
  const [recap, setRecap] = useState(null);
  const [text, setText] = useState('');
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await getRecap(tenantId, eventId);
      setRecap(res.recap);
      setText(res.recap.draft?.content || res.recap.text);
      setPicked(res.recap.suggestedPhotoIds || []);
    } catch {
      setRecap(false);
    }
  }, [tenantId, eventId]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id].slice(0, 10)));

  const saveDraft = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const res = await saveRecapDraft(tenantId, eventId, { content: text, photoIds: picked });
      setNotice({ kind: 'ok', text: t('postEvent.recap.saved', 'Draft saved. It is not posted; schedule it when you are ready.') });
      return res.draft;
    } catch (e) {
      setNotice({ kind: 'error', text: errorText(e, t('postEvent.recap.error', 'Could not save the draft.')) });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const openInScheduler = async () => {
    const d = await saveDraft();
    if (!d) return;
    navigate(`/${tenantId}/${eventId}/schedulePost`, {
      state: { content: d.content, images: d.mediaUrls, eventId: Number(eventId), recapPostId: d.postId },
    });
  };

  if (recap === null) return <section className="surface"><LoadingState /></section>;
  if (recap === false) return null;
  const s = recap.stats;
  return (
    <section className="surface" aria-labelledby="ea-recap-h">
      <h4 id="ea-recap-h">{t('postEvent.recap.title', 'Recap post')}</h4>
      <div className="ea-stats">
        <Stat label={t('postEvent.recap.attendance', 'Checked in')} value={s.attendance} />
        <Stat label={t('postEvent.recap.funds', 'Raised')} value={money(s.fundsRaised, s.currency)} />
        {s.raffleTotal > 0 && <Stat label={t('postEvent.recap.raffle', 'Raffle')} value={money(s.raffleTotal, s.currency)} />}
        {s.auctionTotal > 0 && <Stat label={t('postEvent.recap.auction', 'Auction')} value={money(s.auctionTotal, s.currency)} />}
      </div>
      {!recap.canDraft && <div className="note warn" role="status">{t('postEvent.recap.notReady', 'Recap drafts need the content-approval database update.')}</div>}
      <label className="fo-field" htmlFor="ea-recap-text">{t('postEvent.recap.text', 'Text')}</label>
      <textarea id="ea-recap-text" className="ea-recap-text" value={text} maxLength={5000} disabled={!canEdit}
        onChange={(e) => setText(e.target.value)} />
      <h4>{t('postEvent.recap.photos', 'Photos (approved, OK to repost)')}</h4>
      {recap.photos.length === 0 && <p className="surface-muted perf-small">{t('postEvent.recap.noPhotos', 'No approved photos yet.')}</p>}
      {recap.photos.length > 0 && (
        <ul className="ea-grid">
          {recap.photos.map((p) => (
            <li key={p.id} className={`ea-tile${picked.includes(p.id) ? ' selected' : ''}`}>
              <img src={p.thumbUrl} alt={t('postEvent.photos.alt', 'Photo from {{name}}', { name: p.submitterName || t('postEvent.photos.anon', 'an attendee') })} loading="lazy" />
              <div className="ea-tile-body">
                <label className="ea-pick">
                  <input type="checkbox" checked={picked.includes(p.id)} disabled={!canEdit} onChange={() => toggle(p.id)} />
                  {t('postEvent.recap.use', 'Use in recap')}
                </label>
              </div>
            </li>
          ))}
        </ul>
      )}
      {recap.draft && (
        <p className="perf-small">
          <span className="lm-badge">{t(`postEvent.recap.approval.${recap.draft.approvalStatus}`, recap.draft.approvalStatus)}</span>
          {t('postEvent.recap.draftExists', 'A recap draft exists. Saving again updates it while it is still a draft.')}
        </p>
      )}
      {canEdit && recap.canDraft && (
        <div className="ea-actions">
          <button type="button" className="lm-btn secondary" disabled={busy || (!text.trim() && !picked.length)} onClick={saveDraft}>
            {t('postEvent.recap.saveDraft', 'Save draft')}
          </button>
          <button type="button" className="lm-btn" disabled={busy || (!text.trim() && !picked.length)} onClick={openInScheduler}>
            {t('postEvent.recap.openScheduler', 'Open in scheduler')}
          </button>
        </div>
      )}
      {notice && <div className={`note ${notice.kind}`} role={notice.kind === 'error' ? 'alert' : 'status'}>{notice.text}</div>}
    </section>
  );
}
